import "angular";
import {
  IAttributes,
  IAugmentedJQuery,
  IDocumentService,
  IExceptionHandlerService,
  IHttpProvider,
  INgModelController,
  IQService,
  IRootScopeService,
  IScope,
  auto,
  marked,
  ui,
} from "angular";
import angularDragDrop from "angular-drag-drop";
import dynamicNumber from "angular-dynamic-number";
import "angular-hotkeys";
import { HotkeysProvider } from "angular-hotkeys";
import "angular-marked";
import ngSanitize from "angular-sanitize";
import { IModalStackService } from "angular-ui-bootstrap";
import { Ng1StateDeclaration, RawParams, StateService, TransitionService } from "@uirouter/angularjs";
import { downgradeComponent, downgradeInjectable } from "@angular/upgrade/static";
import "ng-file-upload/dist/ng-file-upload";
import { AuthenticationResolverService } from "@gtmhub/auth";
import { DocumentExporterService } from "@gtmhub/document-exporter/document-exporter-service";
import { FeatureBannerComponent } from "@gtmhub/feature-banner/components/feature-banner.component";
import { feedReducer } from "@gtmhub/feed/redux/feed-reducer";
import { AccountSwitchedCtrl } from "@gtmhub/login/controllers/account-switched-ctrl";
import users from "@gtmhub/users/users.module";
import { FavoriteButtonComponent } from "@webapp/favorites/favorite-button/favorite-button.component";
import NavigationItemsMediator from "@webapp/navigation/services/uxcustomization/navigation-items.mediator.service";
import { AccountNotificationComponent } from "@webapp/notification/components/account-notification/account-notification.component";
import { PersonalNotificationComponent } from "@webapp/notification/components/personal-notification/personal-notification.component";
import { AccountService } from "./accounts/accounts-service";
import announcements from "./announcements/announcements.module";
import { ApiTokensController, IssueTokenCtrl } from "./apiTokens/controllers";
import { ApiTokensService } from "./apiTokens/services";
import { assigneeReducer } from "./assignees";
import assignees from "./assignees/module";
import auth from "./auth/module";
import automation from "./automation/automation.module";
import badges from "./badges/module";
import { BadgesActions } from "./badges/redux/badges-actions";
import { badgesReducer } from "./badges/redux/badges-reducer";
import { TokenRefreshInterceptor } from "./bootstrap/http/token-refresh-interceptor";
import { trackPageLoadTime } from "./bootstrap/performance";
import { trackStateChanges } from "./bootstrap/track-state-changes";
import { CategoryService } from "./categories/CategoryService";
import clickMagick from "./clickMagick/clickMagick.module";
import cloning from "./cloning/module";
import comments from "./comments/module";
import configuration from "./configuration/configuration.module";
import { IsStateActive } from "./configuration/workday/direcives/is-state-active";
import { IsStateInactive } from "./configuration/workday/direcives/is-state-inactive";
import { OrganizationsActions } from "./configuration/workday/redux/organizations-actions";
import { organizationsReducer } from "./configuration/workday/redux/organizations-reducer";
import { ConnectorService } from "./connectors/connectorService";
import core from "./core/module";
import { customFieldsReducer } from "./customFields";
import customFields from "./customFields/module";
import delighted from "./delighted/module";
import editionPlanChange from "./edition-plan-change/edition-plan-change.module";
import { CurrentEmployeeActions, currentEmployeeReducer } from "./employees";
import employeesCore from "./employees/core-module";
import environment from "./env/module";
import errorHandling from "./error-handling/module";
import experiments from "./experiments/experiments.module";
import { FavoritesFactory } from "./favorites/favorites-factory";
import { FavoritesPayloadBuilder } from "./favorites/favorites-payload.builder";
import { favoritesManagerFactory } from "./favorites/favorites.manager.factory";
import featureToggle from "./featureToggle/module";
import feed from "./feed/feed.module";
import goals from "./goals/goals.module";
import { QueryBuilderService } from "./gtmhub/services/queryBuilderService";
import home from "./home/home.module";
import {
  AccountSuspendedResponseInterceptor,
  DisableApiRequestCacheInterceptor,
  HandleUnauthorizedResponseInterceptor,
  ResponseErrorLoggerInterceptor,
  RetryFailedResponseInterceptor,
  RewriteUrlForCdnInterceptor,
  SetAccountIdRequestHeaderInterceptor,
  TrackDataInterceptor,
  UserDeactivatedInterceptor,
} from "./httpInterceptor";
import { FilterFormCtrl } from "./hub/controllers/filter-form";
import { HubService } from "./hub/services/hub";
import insightsCore from "./insights/insights-core.module";
import { WelcomeCtrl } from "./installers/controllers/welcome";
import { InstallerService } from "./installers/services/installerServices";
import { IntegrationService } from "./integrations/services";
import kpis from "./kpis/module";
import links from "./links/links.module";
import lists from "./lists/module";
import localization from "./localization/module";
import { AccountLinkedCtrl } from "./login/controllers/account-linked-ctrl";
import { AccountSuspendedCtrl } from "./login/controllers/account-suspended-ctrl";
import { CreateAccountController } from "./login/controllers/create-account-ctrl";
import { ResolveAccountCtrl } from "./login/controllers/resolve-account-ctrl";
import { ResolveTokenCtrl } from "./login/controllers/resolve-token";
import { UserDeactivatedCtrl } from "./login/controllers/user-deactivated-ctrl";
import { LoginService } from "./login/loginService";
import { InviteUserToAccountService } from "./login/services/inviteUserToAccountService";
import looker from "./looker/module";
import { IGtmhubRootScopeService } from "./models";
import multiAccount from "./multi-account/module";
import navigation from "./navigation/module";
import { KpiInboxTemplateComponent } from "./notifications/components/kpi-inbox-template.component";
import { AccountNotificationSettingsCtrl } from "./notifications/controllers/account-notifications";
import { NotificationsInboxCtrl, NotificationsInboxService } from "./notifications/controllers/notifications-inbox";
import { UserNotificationSettingsCtrl } from "./notifications/controllers/user-notifications";
import notifications from "./notifications/notifications.module";
import alignmentTree from "./okrs/alignment-tree/module";
import metrics from "./okrs/metrics/metrics.module";
import okrs from "./okrs/module";
import onboarding from "./onboarding/onboarding.module";
import { DefineParameterCtrl, ParametersCtrl } from "./parameters/controllers";
import { ParameterService } from "./parameters/parameterService";
import { EffectivePermissionsService } from "./permissions/effective-permissions-service";
import { PluginsActions, pluginsReducer } from "./plugins";
import plugins from "./plugins/plugins.module";
import { PolicyService } from "./policies/services/policyService";
import reflections from "./reflections/reflections.module";
import { currentUserRolesReducer } from "./roles";
import { rolesReducer } from "./roles/redux/roles-reducer";
import roles from "./roles/roles.module";
import search from "./search/search.module";
import { sessionsReducer } from "./sessions";
import sessions from "./sessions/sessions.module";
import sharedComponents from "./shared/components/shared-components.module";
import { DispatchCtrl, ErrorCtrl } from "./shared/controllers";
import { DateRangePickerCtrl } from "./shared/controllers/daterange-picker";
import { IndicatorCtrl } from "./shared/controllers/indicator";
import { UnitFormattingForm, UnitFormattingFormCtrl } from "./shared/controllers/unit-formatting";
import { ArraySelectionDirective } from "./shared/directives/array-selection";
import { Attainment } from "./shared/directives/attainment";
import { Avatar } from "./shared/directives/avatar";
import { ClickOnFirstInput } from "./shared/directives/click-on-first-input";
import { CloseOnMousedown } from "./shared/directives/close-on-mousedown";
import { GhCompileDirective } from "./shared/directives/compile";
import { ConfidenceLevelTrendChart } from "./shared/directives/confidence-level-trend-chart";
import { ConfidencePreview } from "./shared/directives/confidence-preview";
import { ContentMapValueDirective } from "./shared/directives/content-map-value";
import { CountToDirective } from "./shared/directives/count-to";
import { DropdownKeys } from "./shared/directives/dropdown-keys";
import { FocusMe, FocusOn } from "./shared/directives/focus";
import { GhInputSearchComponent } from "./shared/directives/gh-input-search";
import { GtmhubDropdown, GtmhubDropdownItem } from "./shared/directives/gtmhub-dropdown";
import { GtmhubMultiselect } from "./shared/directives/gtmhub-multiselect";
import { InviteLink } from "./shared/directives/invite-link";
import { CmdEnterSubmitDirective, KeydownDirective } from "./shared/directives/keydown";
import { LazySrc } from "./shared/directives/lazy-src";
import { LazySrcWrapper } from "./shared/directives/lazy-src-wrapper";
import { MarketplaceLinkDirective } from "./shared/directives/marketplace-link";
import { NotifyUsersWithoutOwnership } from "./shared/directives/notify-users-without-ownership";
import { RerenderDetectorDirective } from "./shared/directives/rerender-detector";
import { SetConfidence } from "./shared/directives/set-confidence";
import { ToggleDropdown } from "./shared/directives/toggle-dropdown";
import { TrendChart } from "./shared/directives/trend-chart";
import { TrendChartAntiKr } from "./shared/directives/trend-chart-anti-kr";
import { Viewobjectivelist } from "./shared/directives/view-objective-list";
import { CheckAccess } from "./shared/factories/check-permissions";
import sharedFilters from "./shared/filters/filters.module";
import "./shared/libs/chartist";
import mentions from "./shared/mentions/module";
import pagination from "./shared/pagination/module";
import { ErrorService } from "./shared/services";
import { AutoFieldsService } from "./shared/services/auto-fields";
import { HowToStartModalOpenerService } from "./shared/services/how-to-start-modal-opener.service";
import { PageTitleService } from "./shared/services/pageTitleService";
import { SearchService } from "./shared/services/search.service";
import { UpdateService } from "./shared/services/updateService";
import { WebWorkerService } from "./shared/services/webWorkerService";
import { getHrefLinkOptions, getMarkedLinkElement } from "./shared/utils";
import sockets from "./sockets/module";
import { stateManagementModule } from "./state-management/state-management.module";
import { AccountResolverService } from "./state/accountResolverService";
import { SubscriptionService } from "./subscriptions/subscriptionService";
import { TagService } from "./tags/tagService";
import tags from "./tags/tags.module";
import tasks from "./tasks/module";
import { TeamsActions, teamsReducer } from "./teams";
import teamsCore from "./teams/core-module";
import { Commentscount, Timeline } from "./timeline/directives";
import { TimelineService } from "./timeline/timeline.service";
import uiBootstrap from "./ui-bootstrap.module";
import { hasCurrentUserRole } from "./users";
import { CurrentUserService } from "./users/currentUserService";
import { AvatarCtrl } from "./users/userProfile/controllers/avatar.ctrl";
import { SendChangePasswordRequestCtrl } from "./users/userProfile/controllers/send-change-password-request.ctrl";
import { UserLanguageCtrl } from "./users/userProfile/controllers/user-language.ctrl";
import { UserProfileCtrl } from "./users/userProfile/controllers/user-profile.ctrl";
import { UserProfileService } from "./users/userProfile/profileService";
import { persistQueryStringParametersInStorage } from "./util";
import { LastUrlService } from "./util/lastUrlService";
import { ViewHistoryFactory } from "./view-history/view-history-factory";
import { ViewHistoryPayloadBuilder } from "./view-history/view-history-payload.builder";
import { ViewHistoryService } from "./view-history/view-history.service";
import { WelcomeModalV1Component } from "./welcome-modal/components/welcome-modal-v1/welcome-modal-v1";
import whiteboardsCore from "./whiteboards/whiteboards-core.module";
import workflow from "./workflow/module";

const app = angular.module("gtmhub", [
  plugins,
  core,
  auth,
  announcements,
  automation,
  badges,
  sessions,
  goals,
  metrics,
  alignmentTree,
  experiments,
  feed,
  home,
  kpis,
  navigation,
  lists,
  tasks,
  workflow,
  environment,
  localization,
  featureToggle,
  links,
  sockets,
  errorHandling,
  customFields,
  employeesCore,
  teamsCore,
  assignees,
  users,
  configuration,
  editionPlanChange,
  roles,
  onboarding,
  looker,
  insightsCore,
  sharedFilters,
  sharedComponents,
  multiAccount,
  clickMagick,
  okrs,
  cloning,
  whiteboardsCore,
  tags,
  reflections,
  notifications,
  stateManagementModule({
    badges: badgesReducer,
    teams: teamsReducer,
    roles: rolesReducer,
    assignees: assigneeReducer,
    sessions: sessionsReducer,
    customFields: customFieldsReducer,
    currentEmployee: currentEmployeeReducer,
    currentUserRoles: currentUserRolesReducer,
    organizations: organizationsReducer,
    plugins: pluginsReducer,
    feed: feedReducer,
  }),

  // shared
  mentions,
  pagination,
  search,
  delighted,
  comments,
  ngSanitize,
  dynamicNumber,
  "cfp.hotkeys",
  "hc.marked",
  uiBootstrap,
  "ngFileUpload",
  angularDragDrop,
]);

trackPageLoadTime(app);
trackStateChanges(app);

app.controller("ErrorCtrl", ErrorCtrl);
app.controller("IndicatorCtrl", IndicatorCtrl);
app.controller("DispatchCtrl", DispatchCtrl);
app.controller("FilterFormCtrl", FilterFormCtrl);

app.controller("AvatarCtrl", AvatarCtrl);
app.controller("UserNotificationSettingsCtrl", UserNotificationSettingsCtrl);
app.controller("AccountNotificationSettingsCtrl", AccountNotificationSettingsCtrl);
app.controller("NotificationsInboxCtrl", NotificationsInboxCtrl);
app.controller("CreateAccountController", CreateAccountController);
app.controller("DateRangePickerCtrl", DateRangePickerCtrl);
app.controller("UnitFormattingFormCtrl", UnitFormattingFormCtrl);

app.controller("ResolveAccountCtrl", ResolveAccountCtrl);
app.controller("ResolveTokenCtrl", ResolveTokenCtrl);

app.controller("UserProfileCtrl", UserProfileCtrl);
app.controller("SendChangePasswordRequestCtrl", SendChangePasswordRequestCtrl);
app.controller("UserLanguageCtrl", UserLanguageCtrl);
app.controller("UserDeactivatedCtrl", UserDeactivatedCtrl);

app.controller("ParametersCtrl", ParametersCtrl);
app.controller("DefineParameterCtrl", DefineParameterCtrl);

app.controller("ApiTokensController", ApiTokensController);
app.controller("IssueTokenCtrl", IssueTokenCtrl);

app.controller("WelcomeCtrl", WelcomeCtrl);
app.controller("AccountSuspendedCtrl", AccountSuspendedCtrl);
app.controller("AccountLinkedCtrl", AccountLinkedCtrl);
app.controller("AccountSwitchedCtrl", AccountSwitchedCtrl);

app.service("AccountService", AccountService);

app.service("HubService", HubService);

app.service("IntegrationService", IntegrationService);
app.service("NotificationsInboxService", NotificationsInboxService);

app.service("DocumentExporterService", DocumentExporterService);
app.service("QueryBuilderService", QueryBuilderService);

app.service("BadgesActions", BadgesActions);
app.service("TeamsActions", TeamsActions);
app.service("OrganizationsActions", OrganizationsActions);

app.service("CurrentEmployeeActions", CurrentEmployeeActions);
app.service("PluginsActions", PluginsActions);
app.service("LoginService", LoginService);
app.service("InviteUserToAccountService", InviteUserToAccountService);
app.service("ErrorService", ErrorService);
app.service("CategoryService", CategoryService);
app.service("PolicyService", PolicyService);
app.service("ConnectorService", ConnectorService);
app.service("TagService", TagService);
app.service("ParameterService", ParameterService);
app.service("InstallerService", InstallerService);
app.service("ApiTokensService", ApiTokensService);
app.service("TimelineService", TimelineService);
app.service("EffectivePermissionsService", EffectivePermissionsService);
app.service("AccountResolverService", AccountResolverService);
app.service("LastUrlService", LastUrlService);
app.service("SubscriptionService", SubscriptionService);

app.service("UpdateService", UpdateService);
app.service("UnitFormattingForm", UnitFormattingForm);
app.service("WebWorkerService", WebWorkerService);
app.service("SearchService", SearchService);
app.service("PageTitleService", PageTitleService);
app.service("HowToStartModalOpenerService", HowToStartModalOpenerService);
app.service("AutoFieldsService", AutoFieldsService);
app.service("ViewHistoryService", ViewHistoryService);
app.service("FavoritesFactory", FavoritesFactory);
app.service("ViewHistoryFactory", ViewHistoryFactory);
app.service("ViewHistoryPayloadBuilder", ViewHistoryPayloadBuilder);
app.service("FavoritesPayloadBuilder", FavoritesPayloadBuilder);

app.service("NavigationItemsMediator", downgradeInjectable(NavigationItemsMediator));

app.component("ghInputSearch", GhInputSearchComponent);
app.component("featureBanner", FeatureBannerComponent);

app.directive("attainment", Attainment.factory());
app.directive("ghCompile", GhCompileDirective.factory());
app.directive("marketplaceLink", MarketplaceLinkDirective.factory());
app.directive("esc", KeydownDirective.factoryEsc());
app.directive("enter", KeydownDirective.factoryEnter());
app.directive("tabKey", KeydownDirective.factoryTab());
app.directive("cmdEnterSubmit", CmdEnterSubmitDirective.factory());

app.directive("confidencePreview", ConfidencePreview.factory());
app.directive("gtmhubMultiselect", GtmhubMultiselect.factory());
app.directive("gtmhubDropdown", GtmhubDropdown.factory());
app.directive("gtmhubDropdownItem", GtmhubDropdownItem.factory());
app.directive("setConfidence", SetConfidence.factory());
app.directive("closeOnMousedown", CloseOnMousedown.factory());
ArraySelectionDirective.register(app);

app.directive("toggleDropdown", ToggleDropdown.factory());
app.directive("dropdownKeys", DropdownKeys.factory());
CountToDirective.register(app);
app.directive("contentMapValue", ContentMapValueDirective.factory());

app.directive("avatar", Avatar.factory());
app.directive("lazySrc", LazySrc.factory());
app.directive("lazySrcWrapper", LazySrcWrapper.factory());
app.directive("timeline", Timeline.factory());
app.directive("commentscount", Commentscount.factory());
app.directive("trendChart", TrendChart.factory());
app.directive("confidenceLevelTrendChart", ConfidenceLevelTrendChart.factory());
app.directive("trendChartAntiKr", TrendChartAntiKr.factory());
app.directive("focusOn", FocusOn.factory());
app.directive("focusMe", FocusMe.factory());
app.directive("clickOnFirstInput", ClickOnFirstInput.factory());
// the viewobjectivelist directive is used in custom insights' templates
// e.g. Performance report -> OKRs with no progress -> View list link
app.directive("viewobjectivelist", Viewobjectivelist.factory());
app.directive("notifyUsersWithoutOwnership", NotifyUsersWithoutOwnership.factory());
app.directive("inviteLink", InviteLink.factory());
app.directive("rerenderDetector", RerenderDetectorDirective.factory());
app.directive("isStateActive", IsStateActive.factory());
app.directive("isStateInactive", IsStateInactive.factory());
app.directive("ghAccountNotification", downgradeComponent({ component: AccountNotificationComponent }));
app.directive("ghPersonalNotification", downgradeComponent({ component: PersonalNotificationComponent }));
app.directive("ghFavoriteButton", downgradeComponent({ component: FavoriteButtonComponent }));
app.component("welcomeModalV1", WelcomeModalV1Component);
app.component("kpiInboxTemplate", KpiInboxTemplateComponent);

app.factory("CheckAccess", CheckAccess.factory());
app.factory("FavoritesManagerFactory", favoritesManagerFactory);

app.config([
  "$provide",
  function ($provide: auto.IProvideService) {
    $provide.decorator("$exceptionHandler", [
      "$delegate",
      function ($delegate: IExceptionHandlerService) {
        return function (exception, cause) {
          const exceptionsToIgnore = [
            "Possibly unhandled rejection: backdrop click",
            "Possibly unhandled rejection: cancel",
            "Possibly unhandled rejection: escape key press",
          ];

          if (exceptionsToIgnore.includes(exception)) {
            return;
          }

          $delegate(exception, cause);
        };
      },
    ]);
  },
]);

app.config([
  "markedProvider",
  function (markedProvider: marked.IMarkedProvider) {
    markedProvider.setOptions({ sanitize: true });
    markedProvider.setRenderer({
      link: function (href: string) {
        const options = getHrefLinkOptions(href);
        return getMarkedLinkElement(options);
      },
    });
  },
]);

app.directive("uiSrefActiveIf", [
  "$state",
  function ($state: StateService) {
    return {
      restrict: "A",
      controller: [
        "$scope",
        "$transitions",
        "$element",
        "$attrs",
        function ($scope: IScope, $transitions: TransitionService, $element: IAugmentedJQuery, $attrs: IAttributes) {
          const stateName = $attrs.uiSrefActiveIf;

          function update() {
            if ($state.includes(stateName) || $state.is(stateName)) {
              $element.addClass("active");
            } else {
              $element.removeClass("active");
            }
          }

          $scope.$on("$destroy", $transitions.onSuccess({}, update));
          update();
        },
      ],
    };
  },
]);

app.directive("blurOnEnter", [
  function () {
    return {
      restrict: "A",
      link: function (scope: IScope, elm: IAugmentedJQuery) {
        elm.on("keydown", function (evt: JQueryEventObject) {
          const isEnter = evt.keyCode === 13;
          if (isEnter) {
            elm.blur();
            evt.preventDefault();
          }
        });
      },
    };
  },
]);

app.directive("contenteditable", function () {
  return {
    require: "ngModel",
    scope: {
      trustHtml: "@",
    },
    link: function (scope: { trustHtml: string } & IScope, elm: IAugmentedJQuery, attrs: IAttributes, ctrl: INgModelController) {
      // view -> model
      elm.on("keyup", function () {
        ctrl.$setViewValue(elm.text());
      });

      elm.on("blur", function () {
        if (elm.html().length && !elm.text().trim().length) {
          elm.empty();
        }
      });

      // model -> view
      ctrl.$render = function () {
        if (!scope.trustHtml || scope.trustHtml === "true") {
          elm.html(ctrl.$viewValue);
        } else {
          elm.text(ctrl.$viewValue);
        }
      };

      // load init value from DOM
      const eval2 = eval;
      ctrl.$setViewValue(eval2(elm.text()));
    },
  };
});

app.directive("onOutsideElementClick", [
  "$document",
  function ($document: IDocumentService) {
    return {
      restrict: "A",
      link: function (scope, element, attrs) {
        element.on("click", function (e) {
          e.stopPropagation();
        });

        const onClick = function (event: Event) {
          scope.$apply(function () {
            scope.$eval(attrs.onOutsideElementClick, { event });
          });
        };

        $document.on("click", onClick);

        scope.$on("$destroy", function () {
          $document.off("click", onClick);
        });
      },
    };
  },
]);

app.config([
  "$httpProvider",
  ($httpProvider: IHttpProvider) => {
    // Order of insertion is important:
    //   request: first -> last
    //   response: last -> first
    $httpProvider.interceptors.push(
      // request interceptors
      SetAccountIdRequestHeaderInterceptor.factory(),
      RewriteUrlForCdnInterceptor.factory(),
      DisableApiRequestCacheInterceptor.factory(),
      TrackDataInterceptor.factory(),
      TokenRefreshInterceptor.factory(),

      // response interceptors
      ResponseErrorLoggerInterceptor.factory(),
      AccountSuspendedResponseInterceptor.factory(),
      HandleUnauthorizedResponseInterceptor.factory(),
      UserDeactivatedInterceptor.factory(), // Inject the user deactivated interceptor to hijack state redirection if user is deactivated BEFORE other 4xx interceptor checks.
      RetryFailedResponseInterceptor.factory() // this MUST be always last in list, it is going to be executed first in response inteceptors
    );
  },
]);

app.run([
  "$rootScope",
  "$transitions",
  "$state",
  "$q",
  "AuthenticationResolverService",
  "AccountResolverService",
  "CurrentUserService",
  "UserProfileService",
  "LastUrlService",
  (
    $rootScope: IRootScopeService,
    $transitions: TransitionService,
    $state: StateService,
    $q: IQService,
    authenticationResolverService: AuthenticationResolverService,
    accountResolverService: AccountResolverService,
    currentUserService: CurrentUserService,
    profileService: UserProfileService,
    lastUrlService: LastUrlService
  ) => {
    const generateToPathURL = (toState: Ng1StateDeclaration, toStateParams: RawParams): string => {
      // Remove "#"
      const toPath = $state.href(toState.name, toStateParams).slice(1);

      // Remove search from URL. Search is saved in lastUrlService.storeCurrentLocation with $location.search()
      return toPath.split("?")[0];
    };

    $rootScope.$on(
      "$destroy",
      $transitions.onStart({}, (trans) => {
        const toState = trans.to();
        return $q.when(authenticationResolverService.demandsAreMet(toState)).then((demandsAreMet) => {
          if (!demandsAreMet) {
            lastUrlService.storeCurrentLocation(generateToPathURL(toState, trans.params()));

            // deleting all account specific information
            accountResolverService.clear();
            currentUserService.clear();
            authenticationResolverService.clear();

            return authenticationResolverService.navigateToLogin(profileService.getProfile(), { isResolved: false }).then(() => false);
          }

          if (!accountResolverService.demandsAreMet(toState) || !currentUserService.demandsAreMet(toState) || !profileService.demandsAreMet(toState)) {
            lastUrlService.storeCurrentLocation(generateToPathURL(toState, trans.params()));
            return accountResolverService.prepareForResolveAccount();
          }
        });
      })
    );
  },
]);

app.run([
  "$rootScope",
  "$transitions",
  "$state",
  "AccountService",
  "$uibModalStack",
  function (
    $rootScope: IGtmhubRootScopeService,
    $transitions: TransitionService,
    $state: StateService,
    accountService: AccountService,
    $uibModalStack: IModalStackService
  ) {
    $rootScope.hasPermission = function (this: IGtmhubRootScopeService, action: string): boolean {
      return this.effectivePermissions && this.effectivePermissions.indexOf(action) > -1;
    };

    $rootScope.hasRole = function (role: string): boolean {
      return hasCurrentUserRole(role);
    };

    $rootScope.featureAvailable = (name: string): boolean => {
      return accountService.featureAvailable(name);
    };

    $rootScope.editionUsed = (name: string): boolean => {
      return accountService.editionUsed(name);
    };

    $rootScope.$on(
      "$destroy",
      $transitions.onSuccess({}, () => {
        const state = $state.current.name.split(".")[1];
        $rootScope.isMsTeamsState = state === "msteams" || state === "msTeamsHome";
        $rootScope.isGSitesState = state === "gsites";
      })
    );

    const isStatelessModal = (modal: ui.bootstrap.IModalStackedMapKeyValuePair): boolean => {
      return !modal.value.modalDomEl.hasClass("state-initiated");
    };
    $rootScope.$on(
      "$destroy",
      $transitions.onStart({}, () => {
        let modal = $uibModalStack.getTop();

        while (modal && isStatelessModal(modal)) {
          modal.key.close();
          modal = $uibModalStack.getTop();
        }
      })
    );
  },
]);

app.run([
  "$rootScope",
  "UserProfileService",
  "AccountResolverService",
  function ($rootScope: IRootScopeService, profileService: UserProfileService, accountResolverService: AccountResolverService) {
    $rootScope.$on("userSignedOut", () => {
      profileService.clear();
      accountResolverService.clear();
    });
  },
]);

app.run(() => {
  persistQueryStringParametersInStorage("chosenEdition", "feature");
});

app.config([
  "hotkeysProvider",
  (hotkeysProvider: HotkeysProvider) => {
    hotkeysProvider.includeCheatSheet = false;
    hotkeysProvider.useNgRoute = false;
  },
]);

app.config([
  "tagsInputConfigProvider",
  (tagsInputConfigProvider: {
    setActiveInterpolation(field: string, setting: Record<string, unknown>): void;
    setDefaults(field: string, setting: Record<string, unknown>): void;
    setTextAutosizeThreshold(number): void;
  }) => {
    // Needed to be set so that the placeholder can be dynamically changed
    // e.g. in the parent-okr-selector.component.ts
    tagsInputConfigProvider.setActiveInterpolation("tagsInput", { placeholder: true });
  },
]);

export default app;
